import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import cn from "classnames";
import {
  selectActiveProjectId,
  selectSidebarCollapsed,
  setActiveProject,
  setSidebarCollapsed,
} from "@/features/settings";
import ngoLogo from "@/shared/assets/img/logo.png";
import squareLogo from "@/shared/assets/img/logo-sm.png";
import { Skeleton, ScrollArea } from "@/shared/ui";
import { useAuth, useAppDispatch, useAppSelector } from "@/shared/hooks";
import { useGetPendingCountQuery } from "@/features/auth/api";
import { PROJECTS_PAGE_ROUTE, BOARD_PAGE_ROUTE } from "@/shared/utils";
import { settingsSidebarSection, sidebarConfig } from "./config";
import ProjectsButton from "./ProjectsButton";
import SidebarDropdownItem from "./SidebarDropdownItem";
import SidebarLinkItem from "./SidebarLinkItem";
import type { SidebarItem, SidebarSection, SidebarLink, SidebarSubmenu } from "./types";

function isLinkVisible(
  child: SidebarLink,
  hasAnyPermission: (perms: string[]) => boolean,
  hasAnyRole: (roles: string[]) => boolean,
  userType?: string,
): boolean {
  if (child.enabled === false) return false;
  if (userType && child.excludeUserTypes?.includes(userType)) return false;
  const requiredRoles = child.requiredRoles ?? [];
  const requiredPermissions = child.requiredPermissions ?? [];
  if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) return false;
  if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) return false;
  return true;
}

function hasActiveChildInPath(children: (SidebarLink | SidebarSubmenu)[], path: string): boolean {
  return children.some((child) => {
    if (child.type === "submenu") {
      return child.children.some((sub) => path === sub.href || path.startsWith(sub.href + "/"));
    }
    return path === child.href || path.startsWith(child.href + "/");
  });
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    const path = window.location.pathname;
    for (const section of sidebarConfig) {
      for (const item of section.items) {
        if (item.type === "dropdown" && hasActiveChildInPath(item.children, path)) {
          initial[item.label] = true;
        }
      }
    }
    for (const item of settingsSidebarSection.items) {
      if (item.type === "dropdown" && hasActiveChildInPath(item.children, path)) {
        initial[item.label] = true;
      }
    }
    return initial;
  });
  const isCollapsed = useAppSelector(selectSidebarCollapsed);

  const activeProjectId = useAppSelector(selectActiveProjectId);
  const prevActiveProjectIdRef = useRef(activeProjectId);

  const { t } = useTranslation("Sidebar");
  const { user, hasAnyPermission, hasAnyRole } = useAuth();

  const { data: pendingCountData, isLoading: isPendingCountLoading } = useGetPendingCountQuery();

  useEffect(() => {
    const prev = prevActiveProjectIdRef.current;
    prevActiveProjectIdRef.current = activeProjectId;

    if (prev === activeProjectId) return;

    const path = location.pathname;
    const isOnProjectsList = path === PROJECTS_PAGE_ROUTE;
    const kanbanMatch = path.match(/^\/projects\/(\d+)/);
    const isOnKanban = kanbanMatch !== null;

    if (isOnProjectsList && activeProjectId !== null) {
      navigate(`${PROJECTS_PAGE_ROUTE}/${activeProjectId}`);
      return;
    }

    if (isOnKanban && activeProjectId !== null) {
      const currentKanbanId = Number(kanbanMatch![1]);
      if (currentKanbanId !== activeProjectId) {
        navigate(`${PROJECTS_PAGE_ROUTE}/${activeProjectId}`);
      }
    }

    const isOnBoard = path === BOARD_PAGE_ROUTE;
    if (isOnBoard) {
      const currentParams = new URLSearchParams(window.location.search);
      const currentProjectParam = currentParams.get("project");
      const currentProjectId = currentProjectParam ? Number(currentProjectParam) : null;

      if (activeProjectId !== null && currentProjectId !== activeProjectId) {
        currentParams.set("project", String(activeProjectId));
        navigate(`${BOARD_PAGE_ROUTE}?${currentParams.toString()}`);
      } else if (activeProjectId === null && currentProjectParam) {
        currentParams.delete("project");
        currentParams.delete("kpi");
        const qs = currentParams.toString();
        navigate(qs ? `${BOARD_PAGE_ROUTE}?${qs}` : BOARD_PAGE_ROUTE);
      }
    }
  }, [activeProjectId, location.pathname, navigate]);

  function toggleDropdown(label: string) {
    setOpen((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  function handleExpand() {
    dispatch(setSidebarCollapsed(false));
  }

  const filterSidebarItems = useCallback(
    function filterSidebarItems(items: SidebarItem[]): SidebarItem[] {
      return items
        .map((item) => {
          if (item.enabled === false) return null;

          if (item.type === "dropdown") {
            const filteredChildren = item.children
              .map((child) => {
                if (child.type === "submenu") {
                  const filteredSubmenuChildren = child.children.filter((sub) =>
                    isLinkVisible(sub, hasAnyPermission, hasAnyRole, user?.user_type),
                  );
                  if (filteredSubmenuChildren.length === 0) return null;

                  const requiredRoles = child.requiredRoles ?? [];
                  const requiredPermissions = child.requiredPermissions ?? [];
                  if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) return null;
                  if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) return null;

                  return { ...child, children: filteredSubmenuChildren } as SidebarSubmenu;
                }

                if (!isLinkVisible(child, hasAnyPermission, hasAnyRole, user?.user_type)) return null;
                return child;
              })
              .filter((child): child is SidebarLink | SidebarSubmenu => child !== null);

            if (filteredChildren.length > 0) {
              return { ...item, children: filteredChildren };
            }

            const requiredRoles = item.requiredRoles ?? [];
            const requiredPermissions = item.requiredPermissions ?? [];
            if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) return null;
            if (requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) return null;

            return { ...item, children: filteredChildren };
          } else {
            if (!isLinkVisible(item, hasAnyPermission, hasAnyRole, user?.user_type)) return null;
            return item;
          }
        })
        .filter((item): item is SidebarItem => {
          if (item === null) return false;
          if (item.type === "dropdown") {
            return item.children.length > 0;
          }
          return true;
        });
    },
    [hasAnyPermission, hasAnyRole, user?.user_type],
  );

  const pendingCount = pendingCountData?.count ?? undefined;

  function handleProjectsLinkClick() {
    dispatch(setActiveProject(null));
  }

  const enrichedSidebarConfig = useMemo(
    () =>
      sidebarConfig.map((section) => ({
        ...section,
        items: section.items.map((item) => {
          if (item.type === "link" && item.label === "pendingActions") {
            return { ...item, badge: pendingCount };
          }
          if (item.type === "link" && item.label === "projects") {
            return { ...item, onClick: handleProjectsLinkClick };
          }
          return item;
        }),
      })),
    [pendingCount],
  );

  const filteredSidebarConfig = useMemo(
    () =>
      enrichedSidebarConfig
        .map((section) => ({
          ...section,
          items: filterSidebarItems(section.items),
        }))
        .filter((section) => section.items.length > 0),
    [enrichedSidebarConfig, filterSidebarItems],
  );

  const filteredSettingsSection = useMemo(
    () => ({
      ...settingsSidebarSection,
      items: filterSidebarItems(settingsSidebarSection.items),
    }),
    [filterSidebarItems],
  );

  return (
    <nav className="fixed left-0 top-0 h-screen p-5 z-[200]">
      <div
        className={cn(
          "h-full flex flex-col gap-12 justify-between surface-base-fill radius-3xl transition-[width] duration-300 ease-in-out overflow-hidden",
          isCollapsed ? "w-12" : "w-[244px]",
        )}>
        <div className="flex flex-col flex-1 min-h-0">
          <div
            className={cn(
              "flex items-center transition-[padding,gap,opacity] duration-300",
              isCollapsed ? "justify-center px-2 py-4" : "px-5 pt-5 pb-3",
            )}>
            {isCollapsed ? (
              <div className="flex justify-center">
                <img
                  src={squareLogo}
                  className={cn(
                    "h-7 transition-[opacity] duration-300",
                    isCollapsed ? "opacity-100" : "opacity-0 w-0 absolute",
                  )}
                  alt="Logo"
                />
              </div>
            ) : (
              <img
                src={ngoLogo}
                className={cn(
                  "h-7 transition-[opacity] duration-300",
                  isCollapsed ? "opacity-0 w-0 absolute" : "opacity-100",
                )}
                alt="Logo"
              />
            )}
          </div>

          <ScrollArea className="relative flex-1">
            <div className="flex flex-col">
              {filteredSidebarConfig.map((section: SidebarSection, idx: number) => (
                <Fragment key={section.label || idx}>
                  <div className="flex flex-col p-2">
                    {section.label && (
                      <div
                        className={cn(
                          "px-2 py-1.5 text-label-xs content-base-low whitespace-nowrap overflow-hidden transition-[opacity,height] duration-300",
                          isCollapsed ? "opacity-0 h-0 py-0" : "opacity-100",
                        )}>
                        {t(section.label)}
                      </div>
                    )}

                    {section.items.map((item: SidebarItem) =>
                      item.type === "dropdown" ? (
                        <SidebarDropdownItem
                          key={item.label}
                          item={item}
                          isOpen={!!open[item.label]}
                          onToggle={() => toggleDropdown(item.label)}
                          location={location}
                          isCollapsed={isCollapsed}
                          onExpand={handleExpand}
                        />
                      ) : (
                        <SidebarLinkItem key={item.label} item={item} location={location} isCollapsed={isCollapsed} />
                      ),
                    )}
                  </div>
                  {idx === 0 && <ProjectsButton isCollapsed={isCollapsed} onExpand={handleExpand} />}
                </Fragment>
              ))}
            </div>
          </ScrollArea>
        </div>

        <div className="flex flex-col">
          {filteredSettingsSection.items.length > 0 && (
            <div className="flex flex-col border-t surface-container-stroke p-2">
              <div
                className={cn(
                  "px-2 py-1.5 text-label-xs content-base-low whitespace-nowrap overflow-hidden transition-[opacity,height] duration-300",
                  isCollapsed ? "opacity-0 h-0 py-0" : "opacity-100",
                )}>
                {t(filteredSettingsSection.label)}
              </div>

              {filteredSettingsSection.items.map((item: SidebarItem) =>
                item.type === "dropdown" ? (
                  <SidebarDropdownItem
                    key={item.label}
                    item={item}
                    isOpen={!!open[item.label]}
                    onToggle={() => toggleDropdown(item.label)}
                    location={location}
                    isCollapsed={isCollapsed}
                    onExpand={handleExpand}
                  />
                ) : (
                  <SidebarLinkItem key={item.label} item={item} location={location} isCollapsed={isCollapsed} />
                ),
              )}
            </div>
          )}

          {isPendingCountLoading && isCollapsed && <Skeleton className="w-8 h-8 rounded-lg" />}
        </div>
      </div>
    </nav>
  );
}

